import React, { useRef, useState } from 'react'
import { Badge, Button, Form } from 'react-bootstrap'

import PropTypes from 'prop-types'

const CATEGORY_META = {
  diagnosis: {
    label: 'Diagnosis',
    badgeClass: 'bg-primary text-white',
    icon: 'fa-notes-medical',
  },
  provider: {
    label: 'Provider',
    badgeClass: 'bg-info text-dark',
    icon: 'fa-user-md',
  },
  medication: {
    label: 'Medication',
    badgeClass: 'bg-success text-white',
    icon: 'fa-pills',
  },
  allergy: {
    label: 'Allergy',
    badgeClass: 'bg-warning text-dark',
    icon: 'fa-allergies',
  },
  test: {
    label: 'Diagnostic Test',
    badgeClass: 'bg-secondary text-white',
    icon: 'fa-file-medical-alt',
  },
  surgery: {
    label: 'Surgery / Hospitalization',
    badgeClass: 'bg-danger text-white',
    icon: 'fa-procedures',
  },
  injury: {
    label: 'Injury Description',
    badgeClass: 'bg-danger text-white',
    icon: 'fa-band-aid',
  },
}

export default function CaseSuggestionCard({ suggestion, decision, onDecision, onUndo, onDraftChange }) {
  const [editableCanonical, setEditableCanonical] = useState(suggestion.canonicalDescription || '')
  const [isEditing, setIsEditing] = useState(false)
  const candidateButtonRefs = useRef([])
  const [providerFields, setProviderFields] = useState(
    suggestion.canonicalProvider || {
      title: '',
      firstName: '',
      lastName: '',
      suffix: '',
      npi: '',
      practiceName: '',
      phone: '',
      fax: '',
      specialty: '',
      classification: '',
      address: { line1: '', city: '', state: '', zip: '' },
    },
  )

  const notifyDraft = (nextDesc, nextProv, hasExplicitSelection = true) => {
    if (onDraftChange) {
      onDraftChange(suggestion.id, {
        canonicalDescription: nextDesc,
        canonicalProvider: nextProv,
        hasExplicitSelection,
      })
    }
  }

  const meta = CATEGORY_META[suggestion.category] || {
    label: suggestion.category,
    badgeClass: 'bg-secondary text-white',
    icon: 'fa-info-circle',
  }

  const isLongCategory =
    ['surgery', 'diagnosis', 'injury'].includes(suggestion.category?.toLowerCase()) ||
    (editableCanonical && editableCanonical.length > 80)

  const handleProviderFieldChange = (field, val) => {
    const updated = { ...providerFields, [field]: val }
    setProviderFields(updated)

    let nextDesc = editableCanonical
    // Only reconstruct canonical name if name fields changed and not an Organization
    if (
      ['title', 'firstName', 'lastName', 'suffix'].includes(field) &&
      (updated.classification || '').toLowerCase() !== 'organization'
    ) {
      const parts = [updated.title, updated.firstName, updated.lastName].filter(Boolean)
      let displayName = parts.join(' ')
      if (updated.suffix) {
        displayName += `, ${updated.suffix}`
      }
      if (displayName.trim()) {
        nextDesc = displayName
        setEditableCanonical(displayName)
      }
    }
    notifyDraft(nextDesc, updated)
  }

  const handleProviderAddressChange = (addrField, val) => {
    const updatedAddr = { ...(providerFields.address || {}), [addrField]: val }
    const updated = { ...providerFields, address: updatedAddr }
    setProviderFields(updated)
    notifyDraft(editableCanonical, updated)
  }

  const toTitleCaseWords = str => {
    if (!str) return ''
    return String(str)
      .trim()
      .split(/\s+/)
      .map(w => (w.length <= 1 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
      .join(' ')
  }

  const handleSelectNpiCandidate = cand => {
    const candAddr = cand.address || cand.Address || null
    const nextLine1 = cand.addressLine1 ?? cand.AddressLine1 ?? candAddr?.line1 ?? candAddr?.Line1 ?? ''
    const nextLine2 = cand.addressLine2 ?? cand.AddressLine2 ?? candAddr?.line2 ?? candAddr?.Line2 ?? ''
    const nextCity = cand.city ?? cand.City ?? candAddr?.city ?? candAddr?.City ?? ''
    const nextState = cand.state ?? cand.State ?? candAddr?.state ?? candAddr?.State ?? ''
    const nextZip = cand.zip ?? cand.Zip ?? candAddr?.zip ?? candAddr?.Zip ?? ''
    const nextSpecialty =
      cand.primaryTaxonomy || cand.PrimaryTaxonomy || cand.specialty || cand.Specialty || providerFields.specialty || ''

    const rawLabel = cand.label || cand.Label || ''
    const labelPrefix = rawLabel.includes('—') ? rawLabel.split('—')[0].trim() : ''
    let nextCred = (cand.credential || cand.Credential || '').replace(/\./g, '').trim()
    if (!nextCred) {
      const credSource = cand.name || cand.Name || labelPrefix
      const credMatch = credSource && credSource.match(/,\s*([A-Za-z][A-Za-z.\s-]{1,12})$/)
      if (credMatch) {
        nextCred = credMatch[1].replace(/\./g, '').trim()
      }
    }

    const candFirst = toTitleCaseWords(cand.firstName || cand.FirstName || '')
    const candMiddle = toTitleCaseWords(cand.middleName || cand.MiddleName || '')
    const candLast = toTitleCaseWords(cand.lastName || cand.LastName || '')

    const isOrg = (providerFields.classification || '').toLowerCase() === 'organization'
    let nextDesc = editableCanonical
    if (!isOrg) {
      const effFirst = candFirst || providerFields.firstName || ''
      const effLast = candLast || providerFields.lastName || ''
      if (effFirst || effLast) {
        const nameParts = [providerFields.title, effFirst, candMiddle, effLast].filter(Boolean)
        let composed = nameParts.join(' ')
        if (nextCred) {
          composed += `, ${nextCred}`
        }
        if (composed.trim()) {
          nextDesc = composed.trim()
          setEditableCanonical(nextDesc)
        }
      } else if (editableCanonical) {
        const baseWithoutCred = editableCanonical.replace(/,\s*[A-Za-z][A-Za-z.\s-]{1,12}$/, '').trim()
        nextDesc = nextCred ? `${baseWithoutCred}, ${nextCred}` : baseWithoutCred
        setEditableCanonical(nextDesc)
      }
    }

    const updated = {
      ...providerFields,
      name: nextDesc,
      firstName: !isOrg && candFirst ? candFirst : providerFields.firstName,
      lastName: !isOrg && candLast ? candLast : providerFields.lastName,
      npi: cand.npi || cand.Npi || providerFields.npi || '',
      phone: cand.phone ?? cand.Phone ?? '',
      fax: cand.fax ?? cand.Fax ?? '',
      specialty: nextSpecialty,
      suffix: !isOrg ? nextCred : providerFields.suffix || '',
      address: {
        line1: nextLine1,
        line2: nextLine2,
        city: nextCity,
        state: nextState,
        zip: nextZip,
      },
    }
    setProviderFields(updated)
    notifyDraft(nextDesc, updated)
  }

  const handleCandidateKeyDown = (e, currentIdx) => {
    const list = suggestion.npiCandidates || []
    if (list.length === 0) return

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (list.length === 1) {
        handleSelectNpiCandidate(list[0])
        return
      }
      const delta = e.key === 'ArrowDown' ? 1 : -1
      const nextIdx = (currentIdx + delta + list.length) % list.length
      const nextCand = list[nextIdx]
      if (nextCand) {
        handleSelectNpiCandidate(nextCand)
        const nextBtn = candidateButtonRefs.current[nextIdx]
        if (nextBtn && typeof nextBtn.focus === 'function') {
          try {
            nextBtn.focus({ preventScroll: true })
          } catch (_err) {
            nextBtn.focus()
          }
        }
      }
    }
  }

  const handleMerge = () => {
    onDecision(suggestion.id, {
      type: 'merge',
      category: suggestion.category,
      canonicalDescription: editableCanonical.trim(),
      canonicalProvider: suggestion.category === 'provider' ? providerFields : null,
      itemsToMerge: suggestion.rawMergeNames || suggestion.items || [],
    })
  }

  const handleDelete = () => {
    if (suggestion.isRedundantDelete) {
      onDecision(suggestion.id, {
        type: 'delete',
        category: suggestion.category,
        itemToDelete: suggestion.items[0],
      })
    } else {
      // Delete all duplicate items except canonical
      let itemsToDelete = (suggestion.items || []).filter(item => item.trim() !== editableCanonical.trim())
      if (itemsToDelete.length === 0 && (suggestion.items || []).length > 1) {
        itemsToDelete = (suggestion.items || []).slice(1)
      }
      onDecision(suggestion.id, {
        type: 'delete_duplicates',
        category: suggestion.category,
        canonicalToKeep: editableCanonical.trim(),
        itemsToDelete,
      })
    }
  }

  const handleSkip = () => {
    onDecision(suggestion.id, {
      type: 'skip',
      category: suggestion.category,
    })
  }

  // Render decided state
  if (decision) {
    return (
      <div
        className={`card mb-3 border-${
          decision.type === 'skip' ? 'secondary' : decision.type.includes('delete') ? 'danger' : 'success'
        } bg-light`}
      >
        <div className="card-body d-flex align-items-center justify-content-between py-2">
          <div className="d-flex align-items-center">
            <span className="mr-2">
              {decision.type === 'skip' && (
                <i className="fas fa-minus-circle text-muted" style={{ fontSize: '1.25rem' }} />
              )}
              {decision.type === 'merge' && (
                <i className="fas fa-check-circle text-success" style={{ fontSize: '1.25rem' }} />
              )}
              {decision.type.includes('delete') && (
                <i className="fas fa-trash-alt text-danger" style={{ fontSize: '1.25rem' }} />
              )}
            </span>
            <div>
              <span
                className={`badge mr-2 ${
                  decision.type === 'skip'
                    ? 'badge-secondary'
                    : decision.type.includes('delete')
                    ? 'badge-danger'
                    : 'badge-success'
                }`}
              >
                {decision.type === 'skip' ? 'Skipped' : 'Accepted'}
              </span>
              <strong className="text-dark">
                {decision.type === 'skip' && `Skipped "${meta.label}" suggestion`}
                {decision.type === 'merge' && `Merge staged \u2192 "${decision.canonicalDescription}"`}
                {decision.type === 'delete' && `Deletion staged for "${decision.itemToDelete}"`}
                {decision.type === 'delete_duplicates' &&
                  `Duplicates staged for deletion (${decision.itemsToDelete?.length || 0} items)`}
              </strong>
            </div>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => onUndo(suggestion.id)}
            title="Undo decision and reopen as Pending"
          >
            <i className="fas fa-undo mr-1" />
            Undo
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="card mb-3 shadow-sm border">
      <div className="card-header bg-white d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center gap-2">
          <Badge className={meta.badgeClass}>
            <i className={`fas ${meta.icon} mr-1`} />
            {meta.label}
          </Badge>
          <span className="fw-semibold text-dark small">{suggestion.title || 'Duplicate / Inconsistent Entries'}</span>
        </div>
        {suggestion.confidence && (
          <span className="badge bg-light text-dark border">
            {Math.round(suggestion.confidence * 100)}% match confidence
          </span>
        )}
      </div>

      <div className="card-body">
        {suggestion.reason && (
          <div className="alert alert-warning py-1 px-2 mb-3 small d-flex align-items-center">
            <i className="fas fa-lightbulb mr-2 flex-shrink-0" />
            <span>{suggestion.reason}</span>
          </div>
        )}

        <div className="row g-3">
          {/* Left Column: Existing entries in case */}
          <div className="col-md-5 border-end">
            <div className="text-muted small fw-bold mb-2">
              <i className="fas fa-copy mr-1" />
              EXISTING IN CASE ({suggestion.items?.length || 0}):
            </div>
            <ul className="list-group list-group-flush small">
              {(suggestion.items || []).map((item, idx) => (
                <li key={idx} className="list-group-item px-2 py-1 bg-transparent text-break">
                  <i className="fas fa-arrow-right text-muted mr-1" />
                  <span className="fw-medium text-dark">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column: Proposed Standard / Canonical */}
          <div className="col-md-7">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-success small fw-bold">
                <i className="fas fa-check-circle mr-1" />
                {suggestion.isRedundantDelete ? 'PROPOSED ACTION:' : 'PROPOSED STANDARD DESCRIPTION:'}
              </div>
              {!suggestion.isRedundantDelete && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none small"
                  onClick={() => setIsEditing(!isEditing)}
                >
                  <i className="fas fa-pencil-alt mr-1" />
                  {isEditing ? 'Hide Details' : 'Edit'}
                </Button>
              )}
            </div>

            {suggestion.isRedundantDelete ? (
              <div className="p-2 border rounded bg-light text-danger small">
                <i className="fas fa-exclamation-triangle mr-1" />
                Remove contradictory/redundant entry: <strong>{suggestion.items[0]}</strong>
              </div>
            ) : (
              <div>
                {/* Clinical Variations / SME Choice Pills */}
                {suggestion.options && suggestion.options.length > 1 && (
                  <div className="mb-2 p-2 bg-light border rounded">
                    <div className="text-muted small fw-bold mb-1 d-flex align-items-center justify-content-between">
                      <span>
                        <i className="fas fa-sitemap text-primary mr-1" />
                        Clinical Variations (SME Selection):
                      </span>
                      <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                        Click to select code & description
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {suggestion.options.map((opt, oIdx) => {
                        const isSelected =
                          editableCanonical.trim().toLowerCase() === opt.description.trim().toLowerCase() ||
                          (opt.code && editableCanonical.toLowerCase().includes(`(${opt.code.toLowerCase()})`))
                        return (
                          <button
                            key={oIdx}
                            type="button"
                            className={`btn btn-sm ${
                              isSelected ? 'btn-primary shadow-sm text-white' : 'btn-light text-dark shadow-none'
                            } py-1 px-2 d-inline-flex align-items-center`}
                            style={{
                              fontSize: '0.78rem',
                              borderRadius: '14px',
                              border: isSelected ? '1.5px solid #0056b3' : '1.5px solid #6c757d',
                              backgroundColor: isSelected ? undefined : '#ffffff',
                              color: isSelected ? '#ffffff' : '#1f2937',
                              fontWeight: isSelected ? 600 : 500,
                              cursor: 'pointer',
                              transition: 'all 0.15s ease-in-out',
                            }}
                            onClick={() => {
                              setEditableCanonical(opt.description)
                              setIsEditing(false)
                            }}
                          >
                            {isSelected ? (
                              <i className="fas fa-check-circle mr-1 text-white" />
                            ) : (
                              <i className="far fa-circle mr-1 text-muted" style={{ fontSize: '0.7rem' }} />
                            )}
                            <span>{opt.label}</span>
                            {opt.code && (
                              <span
                                className="ms-1 badge"
                                style={{
                                  backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : '#e9ecef',
                                  color: isSelected ? '#ffffff' : '#495057',
                                  fontSize: '0.72rem',
                                  fontWeight: 600,
                                }}
                              >
                                {opt.code}
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                <Form.Control
                  as={isLongCategory ? 'textarea' : 'input'}
                  type={isLongCategory ? undefined : 'text'}
                  rows={isLongCategory ? 3 : undefined}
                  size="sm"
                  className="fw-bold text-dark border-success"
                  value={editableCanonical}
                  onChange={e => {
                    setEditableCanonical(e.target.value)
                    notifyDraft(e.target.value, providerFields)
                  }}
                />

                {/* Proposed Provider Contact Summary (Fixed 3-row layout to prevent height jumps during arrow-key navigation) */}
                {suggestion.category === 'provider' &&
                  (providerFields.address?.line1 ||
                    providerFields.phone ||
                    providerFields.npi ||
                    providerFields.specialty ||
                    (Array.isArray(suggestion.npiCandidates) && suggestion.npiCandidates.length > 0)) && (
                    <div
                      className="mt-2 p-2 rounded border"
                      style={{ backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', fontSize: '0.8rem' }}
                      data-testid="proposed-provider-contact-summary"
                    >
                      <div className="fw-semibold text-success mb-1" style={{ fontSize: '0.75rem' }}>
                        <i className="fas fa-address-card mr-1" />
                        Proposed Contact & Registry Details:
                      </div>
                      <div className="text-dark" style={{ lineHeight: '1.45' }}>
                        {/* Row 1: Primary Taxonomy | Suffix | NPI */}
                        <div className="d-flex align-items-center justify-content-between" style={{ minWidth: 0 }}>
                          <div
                            className="text-truncate mr-2"
                            style={{ flex: '1 1 0', minWidth: 0 }}
                            title={providerFields.specialty || ''}
                          >
                            <i className="fas fa-stethoscope text-success mr-1" />
                            <strong>Primary Taxonomy:</strong> {providerFields.specialty || '—'}
                          </div>
                          <div className="flex-shrink-0 text-nowrap">
                            {(providerFields.classification || '').toLowerCase() !== 'organization' && (
                              <span className="mr-3" data-testid="proposed-provider-suffix">
                                <strong>Suffix:</strong> {providerFields.suffix || '—'}
                              </span>
                            )}
                            <span>
                              <i className="fas fa-hashtag text-muted mr-1" />
                              <strong>NPI:</strong> {providerFields.npi || '—'}
                            </span>
                          </div>
                        </div>
                        {/* Row 2: Street Address */}
                        <div
                          className="text-truncate"
                          style={{ minWidth: 0 }}
                          title={[
                            providerFields.address?.line1,
                            providerFields.address?.line2,
                            providerFields.address?.city,
                            providerFields.address?.state,
                            providerFields.address?.zip,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        >
                          <i className="fas fa-map-marker-alt text-success mr-1" />
                          <strong>Address:</strong>{' '}
                          {[
                            providerFields.address?.line1,
                            providerFields.address?.line2,
                            providerFields.address?.city,
                            providerFields.address?.state,
                            providerFields.address?.zip,
                          ]
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </div>
                        {/* Row 3: Phone | Fax */}
                        <div className="d-flex align-items-center justify-content-between" style={{ minWidth: 0 }}>
                          <div className="text-truncate mr-2" style={{ flex: '1 1 0', minWidth: 0 }}>
                            <i className="fas fa-phone-alt text-success mr-1" />
                            <strong>Phone:</strong> {providerFields.phone || '—'}
                          </div>
                          <div className="text-truncate flex-shrink-0 text-nowrap">
                            <i className="fas fa-fax text-muted mr-1" />
                            <strong>Fax:</strong> {providerFields.fax || '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* NPI Registry Candidate Selector */}
                {suggestion.category === 'provider' &&
                  Array.isArray(suggestion.npiCandidates) &&
                  suggestion.npiCandidates.length > 0 && (
                    <div className="mt-2 p-2 bg-light border rounded" data-testid="npi-candidates-section">
                      <div className="text-muted small fw-bold mb-1 d-flex align-items-center justify-content-between">
                        <span>
                          <i className="fas fa-id-card text-primary mr-1" />
                          NPI Registry Matches ({suggestion.npiCandidates.length}):
                        </span>
                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                          {suggestion.npiCandidates.length > 1
                            ? 'Click a match (or use ↑/↓ arrows) to select & preview'
                            : 'Click to select & populate from NPI Registry'}
                        </span>
                      </div>
                      <div
                        className="d-flex flex-column"
                        style={{ gap: '4px' }}
                        role="listbox"
                        aria-label="NPI Registry Matches"
                      >
                        {suggestion.npiCandidates.map((cand, cIdx) => {
                          const candNpi = cand.npi || cand.Npi || ''
                          const isSelected = Boolean(providerFields.npi && providerFields.npi === candNpi)
                          const candAddr = cand.address || cand.Address || null
                          const line1 =
                            cand.addressLine1 || cand.AddressLine1 || candAddr?.line1 || candAddr?.Line1 || ''
                          const line2 =
                            cand.addressLine2 || cand.AddressLine2 || candAddr?.line2 || candAddr?.Line2 || ''
                          const city = cand.city || cand.City || candAddr?.city || candAddr?.City || ''
                          const state = cand.state || cand.State || candAddr?.state || candAddr?.State || ''
                          const zip = cand.zip || cand.Zip || candAddr?.zip || candAddr?.Zip || ''
                          const streetPart = [line1, line2].filter(Boolean).join(', ')
                          const addrStr = [streetPart, city, [state, zip].filter(Boolean).join(' ')]
                            .filter(Boolean)
                            .join(', ')

                          const firstName = cand.firstName || cand.FirstName || ''
                          const middleName = cand.middleName || cand.MiddleName || ''
                          const lastName = cand.lastName || cand.LastName || ''
                          const credential = cand.credential || cand.Credential || ''
                          const dbaName = cand.dbaName || cand.DbaName || ''
                          const orgName = cand.organizationName || cand.OrganizationName || ''
                          const rawLabel = cand.label || cand.Label || ''
                          const labelPrefix = rawLabel.includes('—') ? rawLabel.split('—')[0].trim() : ''
                          const indivFull = [firstName, middleName, lastName].filter(Boolean).join(' ')
                          const indivDisplay = indivFull ? (credential ? `${indivFull}, ${credential}` : indivFull) : ''
                          const baseCandName =
                            cand.name || cand.Name || indivDisplay || orgName || labelPrefix || 'Provider'
                          const displayHeader =
                            dbaName && dbaName.toLowerCase() !== baseCandName.toLowerCase()
                              ? `${dbaName} (${baseCandName})`
                              : baseCandName
                          const candTaxonomy =
                            cand.primaryTaxonomy || cand.PrimaryTaxonomy || cand.specialty || cand.Specialty || ''
                          const candPhone = cand.phone || cand.Phone || ''
                          const candFax = cand.fax || cand.Fax || ''

                          return (
                            <button
                              key={candNpi || cIdx}
                              ref={el => {
                                candidateButtonRefs.current[cIdx] = el
                              }}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              data-testid={`npi-candidate-option-${cIdx}`}
                              className={`btn btn-sm text-left py-1 px-2 d-flex align-items-start justify-content-between ${
                                isSelected ? 'btn-primary text-white shadow-sm' : 'btn-white border text-dark'
                              }`}
                              style={{
                                fontSize: '0.78rem',
                                borderRadius: '6px',
                                backgroundColor: isSelected ? undefined : '#ffffff',
                              }}
                              onClick={() => handleSelectNpiCandidate(cand)}
                              onKeyDown={e => handleCandidateKeyDown(e, cIdx)}
                            >
                              <div>
                                <div className="fw-bold d-flex align-items-center flex-wrap" style={{ gap: '4px' }}>
                                  <span>
                                    {isSelected ? (
                                      <i className="fas fa-check-circle mr-1" />
                                    ) : (
                                      <i className="far fa-circle mr-1 text-muted" />
                                    )}
                                    {displayHeader}
                                  </span>
                                  {candTaxonomy && (
                                    <span
                                      className={`badge ${
                                        isSelected ? 'badge-light text-dark' : 'badge-secondary text-white'
                                      }`}
                                      style={{ fontSize: '0.68rem' }}
                                      title="Primary Taxonomy"
                                    >
                                      {candTaxonomy}
                                    </span>
                                  )}
                                </div>
                                <div
                                  className={isSelected ? 'text-white' : 'text-muted'}
                                  style={{ fontSize: '0.74rem', paddingLeft: '16px', opacity: isSelected ? 0.92 : 1 }}
                                >
                                  {addrStr && (
                                    <span className="mr-3">
                                      <i className="fas fa-map-marker-alt mr-1" />
                                      {addrStr}
                                    </span>
                                  )}
                                  {candPhone && (
                                    <span className="mr-3">
                                      <i className="fas fa-phone-alt mr-1" />
                                      {candPhone}
                                    </span>
                                  )}
                                  {candFax && (
                                    <span>
                                      <i className="fas fa-fax mr-1" />
                                      {candFax}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {candNpi && (
                                <span
                                  className={`badge ml-2 flex-shrink-0 ${
                                    isSelected ? 'badge-light text-primary' : 'badge-light border text-muted'
                                  }`}
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  NPI: {candNpi}
                                </span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                {/* Provider Decomposition & Contact Editor */}
                {suggestion.category === 'provider' && isEditing && (
                  <div className="mt-2 p-2 border rounded bg-light">
                    <div className="small fw-semibold text-muted mb-2">Provider Elements & Contact Details:</div>
                    <div className="row g-2 small">
                      {(providerFields.classification || '').toLowerCase() !== 'organization' && (
                        <>
                          <div className="col-3">
                            <Form.Label className="mb-0 text-muted">Title</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Dr."
                              value={providerFields.title || ''}
                              onChange={e => handleProviderFieldChange('title', e.target.value)}
                            />
                          </div>
                          <div className="col-4">
                            <Form.Label className="mb-0 text-muted">First Name</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={providerFields.firstName || ''}
                              onChange={e => handleProviderFieldChange('firstName', e.target.value)}
                            />
                          </div>
                          <div className="col-5">
                            <Form.Label className="mb-0 text-muted">Last Name</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={providerFields.lastName || ''}
                              onChange={e => handleProviderFieldChange('lastName', e.target.value)}
                            />
                          </div>
                          <div className="col-3">
                            <Form.Label className="mb-0 text-muted">Suffix</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="MD"
                              value={providerFields.suffix || ''}
                              onChange={e => handleProviderFieldChange('suffix', e.target.value)}
                            />
                          </div>
                        </>
                      )}
                      <div className="col-4">
                        <Form.Label className="mb-0 text-muted">NPI</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={providerFields.npi || ''}
                          onChange={e => handleProviderFieldChange('npi', e.target.value)}
                        />
                      </div>
                      <div className="col-4">
                        <Form.Label className="mb-0 text-muted">Phone</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="555-123-4567"
                          value={providerFields.phone || ''}
                          onChange={e => handleProviderFieldChange('phone', e.target.value)}
                        />
                      </div>
                      <div className="col-4">
                        <Form.Label className="mb-0 text-muted">Fax</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="555-123-4568"
                          value={providerFields.fax || ''}
                          onChange={e => handleProviderFieldChange('fax', e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <Form.Label className="mb-0 text-muted">Street Address</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="123 Medical Way"
                          value={providerFields.address?.line1 || ''}
                          onChange={e => handleProviderAddressChange('line1', e.target.value)}
                        />
                      </div>
                      <div className="col-3">
                        <Form.Label className="mb-0 text-muted">City</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={providerFields.address?.city || ''}
                          onChange={e => handleProviderAddressChange('city', e.target.value)}
                        />
                      </div>
                      <div className="col-1">
                        <Form.Label className="mb-0 text-muted">St</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={providerFields.address?.state || ''}
                          onChange={e => handleProviderAddressChange('state', e.target.value)}
                        />
                      </div>
                      <div className="col-2">
                        <Form.Label className="mb-0 text-muted">Zip</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={providerFields.address?.zip || ''}
                          onChange={e => handleProviderAddressChange('zip', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card Action Buttons: Skip | Accept Suggestion / Merge | Delete Duplicate */}
      <div className="card-footer bg-white d-flex align-items-center justify-content-end py-2" style={{ gap: '8px' }}>
        <Button variant="outline-dark" size="sm" onClick={handleSkip} title="Leave unchanged">
          <i className="fas fa-arrow-right mr-1" />
          Skip
        </Button>

        {suggestion.isRedundantDelete ? (
          <Button variant="danger" size="sm" onClick={handleDelete} title="Accept deletion of redundant entry">
            <i className="fas fa-trash-alt mr-1" />
            Accept Deletion
          </Button>
        ) : (
          <>
            {!suggestion.isEnrichmentOnly && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleDelete}
                title="Delete all duplicate entries except the canonical"
              >
                <i className="fas fa-trash-alt mr-1" />
                Delete Duplicate
              </Button>
            )}
            <Button
              variant="success"
              size="sm"
              onClick={handleMerge}
              disabled={
                !editableCanonical.trim() ||
                (suggestion.isEnrichmentOnly &&
                  !(providerFields.npi || providerFields.address?.line1 || providerFields.phone))
              }
              title={
                suggestion.isEnrichmentOnly &&
                !(providerFields.npi || providerFields.address?.line1 || providerFields.phone)
                  ? 'Select an NPI Registry candidate or enter contact details first'
                  : 'Accept suggestion and merge entries into this standard description'
              }
            >
              <i className="fas fa-check mr-1" />
              Accept Suggestion
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

CaseSuggestionCard.propTypes = {
  suggestion: PropTypes.shape({
    id: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    title: PropTypes.string,
    reason: PropTypes.string,
    confidence: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.string),
    rawMergeNames: PropTypes.arrayOf(PropTypes.string),
    canonicalDescription: PropTypes.string,
    canonicalProvider: PropTypes.object,
    isRedundantDelete: PropTypes.bool,
    isEnrichmentOnly: PropTypes.bool,
    npiCandidates: PropTypes.arrayOf(PropTypes.object),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        code: PropTypes.string,
        description: PropTypes.string,
      }),
    ),
  }).isRequired,
  decision: PropTypes.object,
  onDecision: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  onDraftChange: PropTypes.func,
}
